import { useId } from '@coursera/cds-common';

import type {
  InlineNotificationProps,
  InlineNotification,
} from '@core/notifications';
import { TableBody, TableHead, TableLabel } from '@core/table/core';
import {
  A11yAnnouncerProvider,
  LoadingView,
  NotificationView,
  TableContextProvider,
  TablePaginator,
  ProgressiveLoader,
} from '@core/table/features';
import type { TableProps, TableCommonDefaultProps } from '@core/table/types';

import getTableCss, { classes } from './styles/getTableCss';

/**
 * Tables display data in the form of rows and columns utilized to find, view, synthesize, compare, and or take action on that data.
 *
 * See [Props](__storybookUrl__/components-data-display-table--default#props) for usage info.
 */
const Table = <Row extends Record<string, any>, K extends keyof Row & string>(
  props: TableProps<Row, K>
) => {
  const {
    rows,
    defaultPage,
    defaultSortKey,
    totalRows,
    defaultPageSize,
    defaultSortDirection,
    pageSizeInputOptions,
    rowHeaderAccessor,
    cellVerticalAlign,
    columns,
    maxWidth,
    maxHeight,
    label,
    supportText,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedBy': ariaDescribedBy,
    renderStatus,
    hidePagination,
    status = 'idle',
    serverMode,
    page,
    pageSize,
    sortKey,
    sortDirection,
    onPageChange,
    onPageSizeChange,
    onSort,
    ...rest
  } = props;

  const baseId = useId(id);
  const commonProps: TableCommonDefaultProps<Row, K> = {
    defaultPage: defaultPage,
    defaultPageSize,
    defaultSortDirection,
    defaultSortKey,
    totalRows,
    rowHeaderAccessor,
    cellVerticalAlign,
    id: baseId,
    columns,
    pageSizeInputOptions,
    status,
    hidePagination,
    serverMode,
    page,
    pageSize,
    sortKey,
    sortDirection,
    onPageChange,
    onPageSizeChange,
    onSort,
  };

  let tablebody = null;
  let statusComponent = null;

  switch (status) {
    case 'empty':
      statusComponent = renderStatus?.(status);
      tablebody = null;
      break;
    case 'loading':
      statusComponent = null;
      tablebody = <LoadingView />;
      break;
    case 'error':
      statusComponent = statusComponent = (
        <NotificationView>
          {
            renderStatus?.(status) as React.ReactElement<
              InlineNotificationProps,
              typeof InlineNotification
            >
          }
        </NotificationView>
      );
      tablebody = null;
      break;
    default:
      statusComponent = null;
      tablebody = <TableBody rows={rows} />;
  }

  return (
    <figure css={getTableCss({ maxWidth, maxHeight })}>
      <A11yAnnouncerProvider>
        <TableContextProvider {...commonProps}>
          <TableLabel label={label} supportText={supportText} />
          <div className={classes.scrollWrapper}>
            <table
              aria-describedby={ariaDescribedBy || `${baseId}-support-text`}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy || `${baseId}-label`}
              id={baseId}
              {...rest}
            >
              <TableHead />
              {tablebody}
            </table>
            <ProgressiveLoader />
          </div>
          {!statusComponent && <TablePaginator />}
          {statusComponent}
        </TableContextProvider>
      </A11yAnnouncerProvider>
    </figure>
  );
};

export default Table;
